import { LoadingCircles } from "@frontend/components/elements/LoadingCircles";

import type { TimeSlot } from "@/lib/actions/actions";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { getEarliestBookableTime } from "../bookable-slots";
import { useBooking } from "../context/BookingContext";
import { DateSelector } from "./DateSelector";
import { ProviderList } from "./ProviderList";

interface Provider {
  label: string;
  title?: string;
  value: string;
  img: string | null;
}

interface ProviderSchedulerProps {
  providers: Provider[];
  timeSlots: TimeSlot[];
  isLoading: boolean;
  onTimeSlotSelect: (timeSlot: TimeSlot) => Promise<void>;
  timeZone: string;
  leadTimeDays?: number;
}

export function ProviderScheduler({
  providers,
  timeSlots,
  isLoading,
  onTimeSlotSelect,
  timeZone,
  leadTimeDays,
}: ProviderSchedulerProps) {
  const { currentDisplayDates } = useBooking();
  const t = useTranslations("Shop");
  // One floor for both the date strip and the provider slot list, so a day
  // can't be enabled with nothing bookable under it (PLA-2779).
  const earliestBookableTime = getEarliestBookableTime(timeZone, leadTimeDays);
  const formattedSlotsByProvider = timeSlots?.reduce(
    (acc, slot) => {
      acc[slot.providerId] = acc[slot.providerId] || [];
      acc[slot.providerId].push(slot);
      return acc;
    },
    {} as Record<string, TimeSlot[]>,
  );

  if (isLoading) {
    return (
      <div className="w-full flex mt-[96px]">
        <LoadingCircles />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-stretch gap-4 w-full lg:px-0 sm:px-2">
      <div className="w-full flex justify-between items-center">
        <p className="font-medium text-lg">{t("ChooseAProvider")}</p>
      </div>

      <DateSelector
        timeSlots={timeSlots}
        providers={providers}
        timezone={timeZone}
        leadTimeDays={leadTimeDays}
        earliestBookableTime={earliestBookableTime}
      />
      <ProviderList
        providers={providers}
        formattedSlotsByProvider={formattedSlotsByProvider}
        currentDisplayDates={currentDisplayDates}
        onTimeSlotSelect={onTimeSlotSelect}
        timeZone={timeZone}
        earliestBookableTime={earliestBookableTime}
      />
    </div>
  );
}
